import { TableItem } from 'components/atoms';
import { Table } from 'components/organisms';
import { useFormikContext } from 'formik';
import { useTableColumnsVisibility } from 'hooks/useTableColumnsVisibility';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { formatDate } from 'utils/global/date.utils';

interface Currency {
  currencyCode: string;
  name: string;
  id: number;
}

interface ExchangeRate {
  currencyId: number;
  currency: Currency;
  rate: number;
  exchangeRateDate: string;
  tableCode: string;
}

interface StatementData {
  id: string;
  salesChannelId: string;
  settlementPeriodId: string;
  statementStatus: number;
  defaultCurrency: number;
  defaultTotalValue: number;
  statementFileId: string;
  statementFilePath: string;
  settlementPeriodName: string;
  salesChannelName: string;
  exchangeRates: ExchangeRate[];
}

interface Values {
  exchangeRates: ExchangeRate[];
  isLoading: boolean;
}

interface TableNode {
  id: string;
  currencyCode: string;
  rate: number;
  exchangeRateDate: string;
  tableCode: string;
}

enum SORT_COLUMNS {
  settlementPeriod,
  salesChannel,
  status,
  totalValue,
  currency,
  statementFile,
}

const isValidExchangeRateDate = (exchangeRateDate?: string) => {
  if (!exchangeRateDate?.trim()) {
    return false;
  }

  return !Number.isNaN(new Date(exchangeRateDate).getTime());
};

const ExchangeRates: React.FC = () => {
  const { values } = useFormikContext<Values>();
  const { t } = useTranslation();
  const columnsVisibility = useTableColumnsVisibility(
    'ExchangeRates',
    SORT_COLUMNS
  );
  const exchangeRates: ExchangeRate[] = values?.exchangeRates || [];

  const tableData: TableNode[] = exchangeRates
    .filter(rate => isValidExchangeRateDate(rate.exchangeRateDate))
    .map(rate => ({
      id: rate.currencyId.toString(),
      currencyCode: rate.currency.currencyCode,
      rate: rate.rate,
      exchangeRateDate: rate.exchangeRateDate,
      tableCode: rate.tableCode,
    }));

  return (
    <Table
      id={columnsVisibility.tableId}
      data={{ nodes: tableData }}
      columns={[
        {
          label: t('input.label.tableCode'),
          renderCell: (item: TableNode) => (
            <TableItem>
              <TableItem.Text text={item.tableCode} />
            </TableItem>
          ),
          resize: true,
          hide: columnsVisibility.hide(SORT_COLUMNS.statementFile),
        },
        {
          label: t('input.label.date'),
          renderCell: (item: TableNode) => (
            <TableItem>
              <TableItem.Text text={formatDate(item.exchangeRateDate)} />
            </TableItem>
          ),
          resize: true,
          hide: columnsVisibility.hide(SORT_COLUMNS.settlementPeriod),
        },
        {
          label: t('input.label.currency'),
          renderCell: (item: TableNode) => (
            <TableItem>
              <TableItem.Text text={item.currencyCode} />
            </TableItem>
          ),
          resize: true,
          hide: columnsVisibility.hide(SORT_COLUMNS.currency),
        },
        {
          label: t('input.label.rate'),
          renderCell: (item: TableNode) => (
            <TableItem>
              <TableItem.Text text={item.rate.toFixed(4)} />
            </TableItem>
          ),
          resize: true,
          hide: columnsVisibility.hide(SORT_COLUMNS.totalValue),
        },
      ]}
      displayMode
    />
  );
};

export default ExchangeRates;
